import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { EntityState, ExportState, FilterState } from "./types";

/**
 * UniversalEntityPageUIContext
 *
 * Contains all UI-related state:
 * - filterState: Search, filters, advanced filters, active view
 * - entityState: Editing item, deleting item, modal visibility
 * - exportState: Export progress, export modal visibility
 *
 * This is the VOLATILE half — `filterState.searchTerm` changes on every
 * keystroke. Only components that actually render filter/modal state should
 * consume it; data and config live in their own contexts.
 */
export interface UniversalEntityPageUIContextValue<T extends BaseEntity> {
  filterState: FilterState<T>;
  entityState: EntityState<T>;
  exportState: ExportState;
}

// A React context cannot be generic, so the entity type is erased at the
// storage boundary and re-applied by the hook — the same pattern the god
// context uses. The provider is the only writer, so the two always agree.
const UniversalEntityPageUIContext = createContext<
  UniversalEntityPageUIContextValue<BaseEntity> | undefined
>(undefined);

export function UniversalEntityPageUIProvider<T extends BaseEntity>({
  children,
  value,
}: {
  children: React.ReactNode;
  value: UniversalEntityPageUIContextValue<T>;
}) {
  return (
    <UniversalEntityPageUIContext.Provider
      value={value as unknown as UniversalEntityPageUIContextValue<BaseEntity>}
    >
      {children}
    </UniversalEntityPageUIContext.Provider>
  );
}

/** Non-throwing read — `null` outside a provider. */
export function useOptionalUniversalEntityPageUIContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageUIContextValue<T> | null {
  const context = useContext(UniversalEntityPageUIContext);
  return (context as unknown as UniversalEntityPageUIContextValue<T> | undefined) ?? null;
}

export function useUniversalEntityPageUIContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageUIContextValue<T> {
  const context = useOptionalUniversalEntityPageUIContext<T>();
  if (!context) {
    throw new Error(
      "useUniversalEntityPageUIContext must be used within UniversalEntityPageUIProvider"
    );
  }
  return context;
}
